/*
 * <product-showcase-section> — behavior for sections/section-product-showcase.liquid.
 * Scoped to `this` instead of `document`, so connectedCallback/disconnectedCallback
 * handle Shopify theme-editor re-renders cleanly (no global init flag needed).
 */
(function () {
  if (customElements.get('product-showcase-section')) return;

  customElements.define(
    'product-showcase-section',
    class ProductShowcaseSection extends HTMLElement {
      connectedCallback() {
        this._cleanup = [];
        const on = (target, type, handler) => {
          target.addEventListener(type, handler);
          this._cleanup.push(() => target.removeEventListener(type, handler));
        };

        this._initSlider();
        this._initPurchaseOptions();
        this._initVariantSelect();
        this._initSubscriptionPopup();
      }

      _initSubscriptionPopup() {
        this.querySelectorAll('[data-subscription-popup-trigger]').forEach((trigger) => {
          const dialog = this.querySelector(`#${trigger.dataset.subscriptionPopupTrigger}`);
          if (!dialog) return;

          const openDialog = () => {
            dialog.showModal();
            /* Cart-drawer's own scroll lock is a CSS overflow:hidden class plus event-blocking
             * as a supplement, same as here - a CSS class (not inline style) sidesteps cart-
             * drawer's global guard, which only watches the style attribute for stray inline
             * overflow:hidden locks it didn't set itself. */
            document.documentElement.classList.add('product-showcase-popup-open');
            document.body.classList.add('product-showcase-popup-open');
            requestAnimationFrame(() => requestAnimationFrame(() => dialog.classList.add('is-open')));
          };

          const closeDialog = () => {
            dialog.classList.remove('is-open');
            dialog.addEventListener('transitionend', () => dialog.close(), { once: true });
          };

          dialog.addEventListener('close', () => {
            document.documentElement.classList.remove('product-showcase-popup-open');
            document.body.classList.remove('product-showcase-popup-open');
          });

          /* Belt-and-suspenders on top of the CSS lock above: dialog::backdrop already blocks
           * most pointer interaction with the page behind it, but that alone doesn't reliably
           * stop wheel/touch scroll on every browser. Only let the event through when the
           * target is inside the dialog AND the dialog itself has something to scroll - otherwise
           * (short content, nothing to scroll there) the browser falls through to scrolling the
           * page behind it, same fallthrough as the keydown case below. */
          const blockBackgroundScroll = (e) => {
            if (!dialog.open) return;
            const dialogScrollable = dialog.scrollHeight > dialog.clientHeight + 1;
            if (dialog.contains(e.target) && dialogScrollable) return;
            e.preventDefault();
          };
          document.addEventListener('wheel', blockBackgroundScroll, { passive: false });
          document.addEventListener('touchmove', blockBackgroundScroll, { passive: false });

          /* Focus is trapped inside the dialog while it's modal, but PageDown/Home/End/arrow
           * keys still scroll the document when the dialog itself has nothing to scroll (its
           * content doesn't overflow) - the browser falls back to scrolling the page. Block
           * those specifically; leave Space/Enter alone so the close button still activates. */
          const scrollKeys = ['PageUp', 'PageDown', 'Home', 'End', 'ArrowUp', 'ArrowDown'];
          document.addEventListener('keydown', (e) => {
            if (!dialog.open || !scrollKeys.includes(e.key)) return;
            if (dialog.scrollHeight > dialog.clientHeight + 1) return;
            e.preventDefault();
          });

          trigger.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            openDialog();
          });

          dialog.querySelectorAll('[data-subscription-popup-close]').forEach((closeBtn) => {
            closeBtn.addEventListener('click', closeDialog);
          });

          dialog.addEventListener('click', (e) => {
            if (e.target === dialog) closeDialog();
          });

          dialog.addEventListener('cancel', (e) => {
            e.preventDefault();
            closeDialog();
          });
        });
      }

      _initSlider() {
        const slides = this.querySelectorAll('[data-media-slide]');
        const dots = this.querySelectorAll('[data-media-dot]');
        const track = this.querySelector('[data-media-track]');
        if (slides.length < 2) return;

        const goTo = (index) => {
          const next = (index + slides.length) % slides.length;
          slides.forEach((slide, i) => slide.classList.toggle('is-active', i === next));
          dots.forEach((dot, i) => dot.classList.toggle('is-active', i === next));
          if (track) track.style.transform = `translateX(-${next * 100}%)`;
        };

        const currentIndex = () =>
          Array.from(slides).findIndex((slide) => slide.classList.contains('is-active'));

        this.querySelector('[data-media-prev]')?.addEventListener('click', () => goTo(currentIndex() - 1));
        this.querySelector('[data-media-next]')?.addEventListener('click', () => goTo(currentIndex() + 1));
        dots.forEach((dot) => {
          dot.addEventListener('click', () => goTo(Number(dot.dataset.mediaIndex)));
        });

        const slider = this.querySelector('[data-media-slider]');
        if (slider) {
          let touchStartX = 0;
          slider.addEventListener('touchstart', (e) => { touchStartX = e.touches[0].clientX; }, { passive: true });
          slider.addEventListener('touchend', (e) => {
            const delta = e.changedTouches[0].clientX - touchStartX;
            if (Math.abs(delta) < 40) return;
            goTo(currentIndex() + (delta < 0 ? 1 : -1));
          }, { passive: true });
        }
      }

      _initPurchaseOptions() {
        const options = this.querySelectorAll('[data-purchase-option]');
        const planInput = this.querySelector('[data-selling-plan-input]');
        const benefits = this.querySelector('[data-purchase-benefits]');
        if (!options.length || !planInput) return;

        const applyActive = () => {
          let subscriptionChecked = false;
          options.forEach((option) => {
            const radio = option.querySelector('[data-purchase-radio]');
            option.classList.toggle('is-active', radio.checked);
            if (radio.checked && radio.value !== '') subscriptionChecked = true;
          });
          if (benefits) benefits.classList.toggle('is-active', subscriptionChecked);
          this._updateAtcPrice();
        };

        options.forEach((option) => {
          const radio = option.querySelector('[data-purchase-radio]');
          option.addEventListener('click', () => {
            radio.checked = true;
            planInput.value = radio.value;
            applyActive();
          });
        });
      }

      _initVariantSelect() {
        const select = this.querySelector('[data-variant-select]');
        const variantInput = this.querySelector('[data-variant-id-input]');
        if (!select) return;

        select.addEventListener('change', () => {
          const option = select.selectedOptions[0];
          if (variantInput) variantInput.value = option.value;

          const atc = this.querySelector('[data-add-to-cart]');
          const atcText = this.querySelector('.product-showcase__atc-text');
          const available = option.dataset.available === 'true';
          if (atc) atc.disabled = !available;
          if (atcText) {
            atcText.textContent = available
              ? atcText.dataset.addLabel || atcText.textContent
              : atcText.dataset.soldOutLabel || atcText.textContent;
          }

          const onetimePrice = this.querySelector('[data-purchase-price="onetime"]');
          if (onetimePrice) onetimePrice.textContent = option.dataset.price;

          const planPrice = this.querySelector('[data-purchase-price="plan"]');
          const planComparePrice = this.querySelector('[data-purchase-compare-price="plan"]');
          if (planPrice) planPrice.textContent = option.dataset.planPrice || '';
          if (planComparePrice) planComparePrice.textContent = option.dataset.planComparePrice || '';

          this._updateAtcPrice();
        });
      }

      _updateAtcPrice() {
        const priceEl = this.querySelector('[data-atc-price]');
        if (!priceEl) return;

        const checkedRadio = this.querySelector('[data-purchase-radio]:checked');
        if (!checkedRadio) return;

        const isSubscription = checkedRadio.value !== '';
        const source = isSubscription
          ? this.querySelector('[data-purchase-price="plan"]')
          : this.querySelector('[data-purchase-price="onetime"]');

        if (source && source.textContent.trim()) {
          priceEl.textContent = source.textContent.trim();
        }
      }

      disconnectedCallback() {
        this._cleanup.forEach((fn) => fn());
        this._cleanup = [];
      }
    }
  );
})();
